import { useState, useRef, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { motion, useInView } from "framer-motion";
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation } from "react-router-dom";
import TermsPage from "@/pages/legal/TermsPage";
import PrivacyPage from "@/pages/legal/PrivacyPage";
import AiPage from "@/pages/AiPage";
import { PaymentSuccessPage, PaymentCancelPage } from "@/pages/PaymentStatus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Toaster, toast } from "sonner";
import { 
  Instagram, 
  Linkedin, 
  Youtube,
  Mail,
  ArrowRight,
  Globe,
  TrendingUp,
  Users,
  Award,
  ChevronDown,
  Flag,
  Quote,
  ExternalLink,
  Send,
  Rocket,
  Mic,
  Compass,
  Sparkles
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.4, 0, 0.2, 1] } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.15 } }
};

// Navigation Component
const Navigation = () => {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const navLinks = [
    { id: "about", label: "My Story" },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className="fixed top-0 left-0 right-0 z-50 bg-brand-dark/90 backdrop-blur-md py-4"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <span className="font-serif text-xl md:text-2xl font-semibold text-brand-mist" data-testid="nav-logo">
          Brandi Jo Newman
        </span>
        <div className="flex items-center gap-5 md:gap-8">
          {navLinks.map((link) => (
            <button
              key={link.id}
              onClick={() => scrollTo(link.id)}
              className="font-sans text-sm tracking-wide text-brand-mist/70 hover:text-brand-gold transition-colors hidden sm:block"
              data-testid={`nav-${link.id}`}
            >
              {link.label}
            </button>
          ))}
          <Link
            to="/brandi-jo-ai"
            className="font-sans text-sm tracking-wide bg-brand-gold text-white px-5 py-2 rounded-full hover:bg-brand-tiffany transition-colors shadow-sm"
            data-testid="nav-brandi-jo-ai"
          >
            Brandi Jo AI
          </Link>
        </div>
      </div>
    </motion.nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section
      id="hero"
      className="min-h-screen flex items-center relative overflow-hidden bg-brand-dark"
    >
      {/* Background subtle pattern */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?q=80&w=2070&auto=format&fit=crop')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }} />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-32 md:py-40 w-full">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={stagger}
          className="grid md:grid-cols-2 gap-12 md:gap-16 items-center"
        >
          {/* Text Content */}
          <div className="order-2 md:order-1">
            <motion.h1
              variants={fadeInUp}
              className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-brand-mist leading-[1.1] mb-6"
              data-testid="hero-headline"
            >
              Hi, I'm <span className="text-brand-gold italic">Brandi Jo</span>
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="font-sans text-base md:text-lg text-brand-mist/80 leading-relaxed mb-4"
              data-testid="hero-tagline"
            >
              Former golf touring professional turned entrepreneur.
            </motion.p>
            <motion.p
              variants={fadeInUp}
              className="font-sans text-base md:text-lg text-brand-mist/80 leading-relaxed mb-10 max-w-xl"
            >
              I've built and scaled businesses to 8 figures, led teams of 150+, and competed across 6 continents. 
              My core belief? <span className="text-brand-gold italic">Business is 100% about people.</span>
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })}
                className="btn-gold px-8 py-6 text-base rounded-sm flex items-center gap-2 group"
                data-testid="hero-cta"
              >
                Get To Know Brandi Jo
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </motion.div>
          </div>
          
          {/* Headshot Image */}
          <motion.div 
            variants={fadeInUp}
            className="order-1 md:order-2 flex justify-center md:justify-end"
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-brand-gold/20 rounded-full blur-2xl" />
              <img
                src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR1D81YDvIUJ_aBVREXbAUv236QvYKA0_rMYIkGwmY6MZrpAvklWt7dbxgB&s=10"
                alt="Brandi Jo Newman"
                className="relative w-64 h-64 md:w-80 md:h-80 lg:w-96 lg:h-96 object-cover object-top rounded-full border-2 border-brand-gold/30"
                data-testid="hero-headshot"
              />
            </div>
          </motion.div>
        </motion.div>
      </div>
      
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10"
      >
        <button
          onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })}
          className="text-brand-mist/50 hover:text-brand-gold transition-colors"
          data-testid="scroll-down-btn"
        >
          <ChevronDown className="w-8 h-8 animate-bounce" />
        </button>
      </motion.div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="about" className="py-24 md:py-32 bg-brand-dark" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
          className="grid md:grid-cols-2 gap-12 md:gap-20 items-center"
        >
          <motion.div variants={fadeInUp} className="about-image relative">
            <img
              src="https://customer-assets.emergentagent.com/job_funnel-magic-1/artifacts/qkmd0ide_PXL_20210517_230452121_Original.jpeg"
              alt="Brandi Jo Newman on the golf course"
              className="w-full aspect-[4/5] object-cover object-top rounded-3xl"
              data-testid="about-image"
            />
          </motion.div>
          <motion.div variants={stagger}>
            <motion.p
              variants={fadeInUp}
              className="text-brand-tiffanydeep font-sans text-sm tracking-[0.2em] uppercase mb-4"
            >
              My Story
            </motion.p>
            <motion.h2
              variants={fadeInUp}
              className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-brand-mist mb-6"
              data-testid="about-headline"
            >
              From the <span className="italic text-brand-gold">Fairway</span> to the <span className="italic text-brand-gold">Boardroom</span>
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="font-sans text-brand-mist/80 leading-relaxed mb-6"
            >
              My journey started on golf courses around the world. From 1994 to 2003, I competed professionally 
              on the LPGA Futures Tour, Ladies European Tour, and Asian Tour, playing across Canada, Europe, 
              Asia, India, Japan, and Singapore.
            </motion.p>
            <motion.p
              variants={fadeInUp}
              className="font-sans text-brand-mist/80 leading-relaxed mb-6"
            >
              Golf taught me something invaluable: <span className="text-brand-mist">authentic connection is everything.</span> A 
              Prudential Securities VP once recruited me directly from the golf course because he saw what I've always 
              believed: people do business with people they trust.
            </motion.p>
            <motion.p
              variants={fadeInUp}
              className="font-sans text-brand-mist/80 leading-relaxed mb-8"
            >
              That philosophy has driven every success since: scaling teams from 70 to 150 advisors, 
              building a lead generation platform to 8-figure valuation in under 12 months, and two successful exits.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex gap-8">
              <div>
                <p className="font-serif text-4xl text-brand-tiffanydeep font-semibold">6</p>
                <p className="font-sans text-sm text-brand-mist/50 mt-1">Continents</p>
              </div>
              <div>
                <p className="font-serif text-4xl text-brand-tiffanydeep font-semibold">8</p>
                <p className="font-sans text-sm text-brand-mist/50 mt-1">Figure Exit</p>
              </div>
              <div>
                <p className="font-serif text-4xl text-brand-tiffanydeep font-semibold">150+</p>
                <p className="font-sans text-sm text-brand-mist/50 mt-1">Team Scaled</p>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Journey/Experience Section
const JourneySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const highlights = [
    {
      icon: Globe,
      title: "Professional Golfer",
      period: "1994 – 2003",
      description: "Competed on the LPGA Futures Tour, Ladies European Tour, and Asian Tour, traveling around the world twice across 6 continents. It was a PhD in culture, language, and the universal currency of real human connection.",
    },
    {
      icon: Rocket,
      title: "Born to Build",
      period: "A Lifelong Pursuit",
      description: "An entrepreneur since day one. I've launched, scaled, and exited businesses across industries, including a lead generation platform taken to 8-figure valuation in under 12 months, two successful exits, and a boutique retirement income firm my team now runs without me. Building is simply what I do.",
    },
    {
      icon: Users,
      title: "Sales Leadership",
      period: "2003 – 2015",
      description: "Built and scaled advisor teams in the financial services industry, from 70 to 150 at the corporate level and 55 across Texas at the regional level. Full P&L responsibility, recruiting, and strategic partnerships.",
    },
    {
      icon: Award,
      title: "Community Impact",
      period: "2017 – 2022",
      description: "Volunteered with The First Tee of San Antonio, teaching youth life skills through golf. Giving back while maintaining industry connections.",
    },
  ];

  return (
    <section id="journey" className="py-24 md:py-32 bg-brand-blue/40" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
          className="text-center mb-16"
        >
          <motion.p
            variants={fadeInUp}
            className="text-brand-tiffanydeep font-sans text-sm tracking-[0.2em] uppercase mb-4"
          >
            The Journey
          </motion.p>
          <motion.h2
            variants={fadeInUp}
            className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-brand-mist"
            data-testid="journey-headline"
          >
            Connection, Validation, <span className="italic text-brand-gold">Trust</span>
          </motion.h2>
        </motion.div>
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
          className="grid md:grid-cols-2 gap-6"
        >
          {highlights.map((item, index) => (
            <motion.div
              key={item.title}
              variants={fadeInUp}
              className="bento-card p-8 md:p-10 rounded-sm"
              data-testid={`journey-card-${index}`}
            >
              <div className="flex items-center gap-4 mb-4">
                <item.icon className="w-8 h-8 text-brand-gold" />
                <span className="font-sans text-sm text-brand-tiffanydeep">{item.period}</span>
              </div>
              <h3 className="font-serif text-2xl text-brand-mist mb-4">{item.title}</h3>
              <p className="font-sans text-brand-mist/70 leading-relaxed">{item.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Philosophy Section
const PhilosophySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="py-24 md:py-32 bg-brand-dark" ref={ref}>
      <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
        >
          <motion.p
            variants={fadeInUp}
            className="text-brand-tiffanydeep font-sans text-sm tracking-[0.2em] uppercase mb-8"
          >
            Core Philosophy
          </motion.p>
          <motion.blockquote
            variants={fadeInUp}
            className="font-serif text-2xl sm:text-3xl lg:text-4xl text-brand-mist leading-relaxed mb-8"
            data-testid="philosophy-quote"
          >
            "Business is <span className="text-brand-gold italic">100% about people</span>: the connection, 
            validation, and trust that enables growth and prosperity."
          </motion.blockquote>
          <motion.div variants={fadeInUp} className="section-divider w-24 mx-auto" />
        </motion.div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const testimonials = [
    {
      quote: "Brandi Jo helped me see my retirement and my business as one integrated plan, not two separate problems. She asks the right questions, listens deeply, and makes you feel genuinely seen. The clarity and confidence I have now is worth every conversation.",
      author: "Sandra Sell",
      role: "Retirement Income & Business Client",
    },
  ];

  return (
    <section id="testimonials" className="py-24 md:py-32 bg-brand-blue/30" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
          className="text-center mb-16"
        >
          <motion.p
            variants={fadeInUp}
            className="text-brand-tiffanydeep font-sans text-sm tracking-[0.2em] uppercase mb-4"
          >
            Kind Words
          </motion.p>
          <motion.h2
            variants={fadeInUp}
            className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-brand-mist"
            data-testid="testimonials-headline"
          >
            From People I've <span className="italic text-brand-gold">Worked With</span>
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
          className="max-w-3xl mx-auto"
        >
          {testimonials.map((t, idx) => (
            <motion.figure
              key={t.author}
              variants={fadeInUp}
              className="relative bg-brand-dark/40 backdrop-blur-sm border border-brand-gold/15 rounded-sm p-10 md:p-14"
              data-testid={`testimonial-${idx}`}
            >
              <Quote className="absolute top-8 left-8 w-10 h-10 text-brand-tiffany/50" strokeWidth={1.25} />
              <blockquote className="font-serif text-xl md:text-2xl text-brand-mist/90 leading-relaxed italic pl-8 md:pl-12 mb-8">
                {t.quote}
              </blockquote>
              <figcaption className="flex items-center gap-4 pl-8 md:pl-12">
                <div className="h-px w-12 bg-brand-gold" />
                <div>
                  <p className="font-serif text-lg text-brand-mist">{t.author}</p>
                  <p className="font-sans text-sm text-brand-mist/50">{t.role}</p>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Newsletter Section — Substack
const NewsletterSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="newsletter" className="py-24 md:py-32 bg-brand-dark" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
          className="relative overflow-hidden border border-brand-gold/20 rounded-sm px-8 py-14 md:px-16 md:py-20"
        >
          {/* Gold ambient glow */}
          <div className="absolute -top-20 -right-20 w-80 h-80 bg-brand-gold/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-20 w-72 h-72 bg-brand-gold/5 rounded-full blur-3xl pointer-events-none" />

          <div className="relative grid md:grid-cols-5 gap-10 items-center">
            <div className="md:col-span-3">
              <motion.p
                variants={fadeInUp}
                className="text-brand-tiffanydeep font-sans text-sm tracking-[0.2em] uppercase mb-4"
              >
                The Newsletter
              </motion.p>
              <motion.h2
                variants={fadeInUp}
                className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-brand-mist mb-6"
                data-testid="newsletter-headline"
              >
                Thoughts on <span className="italic text-brand-gold">business, people,</span> and the long game.
              </motion.h2>
              <motion.p
                variants={fadeInUp}
                className="font-sans text-brand-mist/80 leading-relaxed max-w-xl"
              >
                Lessons from the fairway, the boardroom, and everywhere in between, delivered straight to your inbox on Substack.
              </motion.p>
            </div>
            <motion.div variants={fadeInUp} className="md:col-span-2 flex md:justify-end">
              <a
                href="https://brandijo7671.substack.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold inline-flex items-center gap-3 px-8 py-5 text-base rounded-sm group"
                data-testid="newsletter-subscribe-btn"
              >
                <Send className="w-4 h-4 group-hover:-rotate-12 transition-transform" />
                Subscribe on Substack
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    interest: "connect",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, formData);
      toast.success("Message sent! I'll be in touch soon.");
      setFormData({ name: "", email: "", phone: "", message: "", interest: "connect" });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-brand-blue/30" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
          className="grid md:grid-cols-2 gap-16 md:gap-24"
        >
          <motion.div variants={stagger}>
            <motion.p
              variants={fadeInUp}
              className="text-brand-tiffanydeep font-sans text-sm tracking-[0.2em] uppercase mb-4"
            >
              Get in Touch
            </motion.p>
            <motion.h2
              variants={fadeInUp}
              className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-brand-mist mb-6"
              data-testid="contact-headline"
            >
              Let's <span className="italic text-brand-gold">Connect</span>
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="font-sans text-brand-mist/80 leading-relaxed mb-8"
            >
              Whether you're interested in collaboration, have a question, or just want to say hello, I'd love to hear from you. 
              The best conversations start with a simple connection.
            </motion.p>
            <motion.div variants={fadeInUp} className="space-y-4 mb-8">
              <div className="flex items-center gap-4">
                <Mail className="w-5 h-5 text-brand-gold" />
                <a href="mailto:me@brandijo.com" className="font-sans text-brand-mist/80 hover:text-brand-gold transition-colors">
                  me@brandijo.com
                </a>
              </div>
            </motion.div>
            <motion.div variants={fadeInUp} className="flex items-center gap-6">
              <a
                href="https://instagram.com/brandijonewman"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-brand-mist/70 hover:text-brand-gold transition-colors"
                data-testid="contact-instagram"
              >
                <Instagram className="w-5 h-5" />
                <span className="font-sans text-sm">Instagram</span>
              </a>
              <a
                href="https://linkedin.com/in/brandijonewman"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-brand-mist/70 hover:text-brand-gold transition-colors"
                data-testid="contact-linkedin"
              >
                <Linkedin className="w-5 h-5" />
                <span className="font-sans text-sm">LinkedIn</span>
              </a>
              <a
                href="https://youtube.com/@brandijonewman365"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-brand-mist/70 hover:text-brand-gold transition-colors"
                data-testid="contact-youtube"
              >
                <Youtube className="w-5 h-5" />
                <span className="font-sans text-sm">YouTube</span>
              </a>
            </motion.div>
          </motion.div>
          <motion.form
            variants={fadeInUp}
            onSubmit={handleSubmit}
            className="space-y-6"
            data-testid="contact-form"
          >
            <Input
              type="text"
              name="name"
              placeholder="Your Name"
              value={formData.name}
              onChange={handleChange}
              required
              className="luxury-input w-full bg-transparent border-0 border-b border-brand-gold/30 rounded-none px-0 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              data-testid="contact-name-input"
            />
            <Input
              type="email"
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleChange}
              required
              className="luxury-input w-full bg-transparent border-0 border-b border-brand-gold/30 rounded-none px-0 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              data-testid="contact-email-input"
            />
            <Input
              type="tel"
              name="phone"
              placeholder="Phone (Optional)"
              value={formData.phone}
              onChange={handleChange}
              className="luxury-input w-full bg-transparent border-0 border-b border-brand-gold/30 rounded-none px-0 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              data-testid="contact-phone-input"
            />
            <Textarea
              name="message"
              placeholder="What's on your mind?"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className="luxury-input w-full bg-transparent border-0 border-b border-brand-gold/30 rounded-none px-0 resize-none focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              data-testid="contact-message-input"
            />
            <Button
              type="submit"
              disabled={loading}
              className="btn-gold w-full py-6 text-base rounded-sm flex items-center justify-center gap-2"
              data-testid="contact-submit-btn"
            >
              {loading ? (
                <span className="spinner" />
              ) : (
                <>
                  Send Message
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </motion.form>
        </motion.div>
      </div>
    </section>
  );
};

// Now Chapter Section — Exit Without Selling
const NowChapterSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="now" className="py-24 md:py-32 bg-brand-dark relative overflow-hidden" ref={ref}>
      <div className="absolute -top-24 right-0 w-[500px] h-[500px] bg-brand-gold/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
          className="grid md:grid-cols-2 gap-12 md:gap-20 items-center"
        >
          <motion.div variants={stagger}>
            <motion.p
              variants={fadeInUp}
              className="text-brand-tiffanydeep font-sans text-sm tracking-[0.2em] uppercase mb-4"
            >
              The Chapter I'm Living Now
            </motion.p>
            <motion.h2
              variants={fadeInUp}
              className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-brand-mist mb-6 leading-tight"
              data-testid="now-headline"
            >
              The Best Seat I've Ever Held: <span className="italic text-brand-gold">Co-Pilot</span>
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="font-sans text-brand-mist/80 leading-relaxed mb-6"
            >
              After a career of building, scaling, and exiting my own businesses, I've stepped into
              the seat I was always meant for: <span className="text-brand-mist">Co-Pilot Advisor with Exit Without Selling.</span>
            </motion.p>
            <motion.p
              variants={fadeInUp}
              className="font-sans text-brand-mist/80 leading-relaxed mb-6"
            >
              I sit beside fellow entrepreneurs and small business owners, the ones who built something
              real, and help them exit the day-to-day without selling the business they poured their
              lives into. They keep flying the plane. I make sure they're finally free to enjoy the view.
            </motion.p>
            <motion.p
              variants={fadeInUp}
              className="font-serif text-xl md:text-2xl text-brand-mist italic leading-relaxed"
            >
              This isn't another venture. It's my way of <span className="text-brand-gold">giving back</span>,
              and the most meaningful work of my career.
            </motion.p>
          </motion.div>

          <motion.div variants={fadeInUp}>
            <a
              href="https://www.exitwithoutselling.com"
              target="_blank"
              rel="noopener noreferrer"
              className="group block relative overflow-hidden rounded-sm border border-brand-gold/20 hover:border-brand-gold/60 transition-all duration-500"
              data-testid="now-ews-card"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-brand-gold/5 via-brand-dark to-brand-blue/20 group-hover:from-brand-gold/10 transition-all duration-700" />
              <div className="relative p-8 md:p-12">
                <div className="inline-flex items-center gap-2 px-3 py-1 border border-brand-gold/30 rounded-full mb-8">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-tiffany animate-pulse" />
                  <span className="font-sans text-xs tracking-[0.15em] uppercase text-brand-gold">Co-Pilot Advisor</span>
                </div>
                <Compass className="w-12 h-12 text-brand-gold mb-6" strokeWidth={1.5} />
                <p className="font-sans text-sm tracking-[0.2em] uppercase text-brand-mist/50 mb-2">
                  For Owners Ready For Freedom
                </p>
                <h3 className="font-serif text-3xl md:text-4xl text-brand-mist mb-6 leading-tight">
                  Exit Without <span className="italic text-brand-gold">Selling</span>
                </h3>
                <p className="font-sans text-brand-mist/80 leading-relaxed mb-8">
                  Step out of the day-to-day. Keep the business you built. A proven path from
                  owner-dependence to a team-led company, and a life you actually get to live.
                </p>
                <div className="inline-flex items-center gap-3 text-brand-gold group-hover:gap-5 transition-all duration-300">
                  <span className="font-sans text-base tracking-wide">Visit exitwithoutselling.com</span>
                  <ExternalLink className="w-5 h-5" />
                </div>
              </div>
              <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-brand-gold group-hover:w-full transition-all duration-700" />
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Projects Section — Featured Venture
const ProjectsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="projects" className="py-24 md:py-32 bg-brand-dark relative overflow-hidden" ref={ref}>
      {/* Ambient gold glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-gold/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
          className="text-center mb-16"
        >
          <motion.p
            variants={fadeInUp}
            className="text-brand-tiffanydeep font-sans text-sm tracking-[0.2em] uppercase mb-4"
          >
            Ventures & Projects
          </motion.p>
          <motion.h2
            variants={fadeInUp}
            className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-brand-mist"
            data-testid="projects-headline"
          >
            Where I'm <span className="italic text-brand-gold">Building</span>
          </motion.h2>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
          className="max-w-4xl mx-auto space-y-8"
        >
          {/* Ridgemont Financial — Exited Without Selling */}
          <motion.a
            href="https://ridgemontfinancial.com"
            target="_blank"
            rel="noopener noreferrer"
            variants={fadeInUp}
            className="group block relative overflow-hidden rounded-sm border border-brand-gold/20 hover:border-brand-gold/60 transition-all duration-500"
            data-testid="featured-project-card"
          >
            {/* Gradient background layer */}
            <div className="absolute inset-0 bg-gradient-to-br from-brand-blue/40 via-brand-dark to-brand-blue/20 group-hover:from-brand-gold/10 group-hover:via-brand-dark group-hover:to-brand-blue/30 transition-all duration-700" />

            {/* Content */}
            <div className="relative grid md:grid-cols-5 gap-8 md:gap-12 p-8 md:p-12 lg:p-16">
              {/* Left: Icon + Label */}
              <div className="md:col-span-2 flex flex-col justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 border border-brand-gold/30 rounded-full mb-6">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-tiffany animate-pulse" />
                    <span className="font-sans text-xs tracking-[0.15em] uppercase text-brand-gold">Exited Without Selling</span>
                  </div>
                  <TrendingUp className="w-12 h-12 text-brand-gold mb-6" strokeWidth={1.5} />
                  <p className="font-sans text-sm tracking-[0.2em] uppercase text-brand-mist/50 mb-2">
                    Retirement Income Planning
                  </p>
                  <h3 className="font-serif text-3xl md:text-4xl text-brand-mist transition-colors">
                    Ridgemont <span className="italic text-brand-gold">Financial</span>
                  </h3>
                </div>
              </div>

              {/* Right: Value prop + CTA */}
              <div className="md:col-span-3 flex flex-col justify-between gap-8">
                <div className="space-y-4">
                  <p className="font-sans text-base md:text-lg text-brand-mist/80 leading-relaxed">
                    I built Ridgemont. Then I exited without selling it. <span className="text-brand-mist">My team has been
                    unleashed to run it as they see fit,</span> maximizing their own skills and talents. Ridgemont does one
                    thing, and does it well: retirement income plans. We help W-2 employees max out their retirement
                    plans for income, long-term care, and passing on assets without unnecessary taxes.
                  </p>
                  <p className="font-sans text-sm text-brand-mist/50 leading-relaxed italic">
                    And quietly, the team always has room for one more seasoned, licensed advisor who closes with heart.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-6">
                  <div className="inline-flex items-center gap-3 text-brand-gold group-hover:gap-5 transition-all duration-300">
                    <span className="font-sans text-base tracking-wide">Visit ridgemontfinancial.com</span>
                    <ExternalLink className="w-5 h-5" />
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom accent line */}
            <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-brand-gold group-hover:w-full transition-all duration-700" />
          </motion.a>

          {/* Golf Lifestyle Network */}
          <motion.div variants={fadeInUp}>
            <div
              className="group relative overflow-hidden rounded-sm border border-brand-gold/20 hover:border-brand-gold/60 transition-all duration-500"
              data-testid="golf-network-card"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-brand-gold/5 via-brand-dark to-brand-blue/20 group-hover:from-brand-gold/10 transition-all duration-700" />

              <div className="relative grid md:grid-cols-5 gap-8 md:gap-12 p-8 md:p-12 lg:p-16">
                <div className="md:col-span-2 flex flex-col justify-between">
                  <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 border border-brand-gold/30 rounded-full mb-6">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-tiffany animate-pulse" />
                      <span className="font-sans text-xs tracking-[0.15em] uppercase text-brand-gold">The Network · 3 Cities</span>
                    </div>
                    <Flag className="w-12 h-12 text-brand-gold mb-6" strokeWidth={1.5} />
                    <p className="font-sans text-sm tracking-[0.2em] uppercase text-brand-mist/50 mb-2">
                      Golf Lifestyle Network
                    </p>
                    <h3 className="font-serif text-3xl md:text-4xl text-brand-mist leading-tight">
                      Where the golf life is <span className="italic text-brand-gold">lived.</span>
                    </h3>
                  </div>
                </div>

                <div className="md:col-span-3 flex flex-col justify-between gap-8">
                  <p className="font-sans text-base md:text-lg text-brand-mist/80 leading-relaxed">
                    A network of local golf lifestyle communities connecting golfers to <span className="text-brand-mist">the courses,
                    the homes, and the life</span> in the country's best golf cities.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {[
                      { name: "Scottsdale", href: "https://scottsdalegolflifestyle.com", img: "https://customer-assets-rejwkqb3.emergentagent.net/job_4e1ed2e0-0184-492d-8da2-b39614f28bf5/artifacts/4kp9pmke_troon-north-golf-club-1.jpg", id: "scottsdale" },
                      { name: "La Quinta", href: "https://laquintagolflifestyle.com", img: "https://static.prod-images.emergentagent.com/jobs/4e1ed2e0-0184-492d-8da2-b39614f28bf5/images/7297e94927cd1904dd9fac8061099d2ab07cb655761e38b43c8732f34853cb78.jpeg", id: "laquinta" },
                      { name: "Palm Beach", href: "https://palmbeachgolflifestyle.com", img: "https://static.prod-images.emergentagent.com/jobs/4e1ed2e0-0184-492d-8da2-b39614f28bf5/images/c281394b0a232572956a35ff188c6238aa1388ca66988c29ec7fef40efe8a4b3.jpeg", id: "palmbeach" },
                    ].map((city) => (
                      <a
                        key={city.id}
                        href={city.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group/city relative block overflow-hidden rounded-sm border border-brand-gold/20 hover:border-brand-gold/70 transition-colors duration-500"
                        data-testid={`golf-link-${city.id}`}
                      >
                        <img
                          src={city.img}
                          alt={`${city.name} golf lifestyle`}
                          className="w-full h-36 md:h-40 object-cover transition-transform duration-700 group-hover/city:scale-105"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-3 flex items-center justify-between">
                          <span className="font-serif text-lg text-white">{city.name}</span>
                          <ExternalLink className="w-4 h-4 text-brand-gold opacity-70 group-hover/city:opacity-100 transition-opacity" />
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-brand-gold group-hover:w-full transition-all duration-700" />
            </div>
          </motion.div>

          {/* The Podcast — Coming Soon mention */}
          <motion.div variants={fadeInUp}>
            <div
              className="relative overflow-hidden rounded-sm border border-brand-gold/15 bg-brand-blue/20"
              data-testid="podcast-mention-card"
            >
              <div className="relative p-6 md:p-10">
                <div className="flex flex-col sm:flex-row sm:items-start gap-6">
                  <div className="flex items-start gap-5 flex-1">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full border border-brand-gold/30 flex items-center justify-center">
                      <Mic className="w-5 h-5 text-brand-tiffany" strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <p className="font-sans text-xs tracking-[0.2em] uppercase text-brand-mist/50 mb-2">
                        The Podcast
                      </p>
                      <h3 className="font-serif text-2xl md:text-3xl text-brand-mist leading-tight mb-2">
                        The Brandi Jo <span className="italic text-brand-gold">Show</span>
                      </h3>
                      <p className="font-sans text-sm md:text-base tracking-wide text-brand-gold/90 italic mb-5">
                        Spirit. Soul. And Story.
                      </p>
                      <p className="font-sans text-sm md:text-base text-brand-mist/80 leading-relaxed">
                        The Brandi Jo Show is back, and this season we stop asking who you've been and start asking who you came here to be. <span className="text-brand-mist">Spirit. Soul. And Story.</span> Three words, one filter. Every episode pulls a real person into the space between the life they were handed and the life they're meant to build. No performance. No polish for its own sake. Just the raw work of remembering who you are and the stories of the people doing it. This season starts in the heart, with Brandi Jo and her wife, Claudia, and it doesn't let up. <span className="italic text-brand-gold">A calling isn't found. It's crafted.</span> Come build yours. No pressure. Only presence.
                      </p>
                    </div>
                  </div>
                  <div className="sm:flex-shrink-0">
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-brand-gold/30 rounded-full">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-tiffany animate-pulse" />
                      <span className="font-sans text-xs tracking-[0.15em] uppercase text-brand-gold">Coming Soon</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

        </motion.div>
      </div>
    </section>
  );
};

// Ripple Section
const RippleSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="py-24 bg-brand-dark relative overflow-hidden" ref={ref}>
      <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={fadeInUp}
        >
          <div className="flex items-center justify-center gap-3 mb-8" aria-hidden="true">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-tiffany/30" />
            <span className="w-2 h-2 rounded-full bg-brand-tiffany/50" />
            <span className="w-2.5 h-2.5 rounded-full bg-brand-tiffany" />
            <span className="w-2 h-2 rounded-full bg-brand-tiffany/50" />
            <span className="w-1.5 h-1.5 rounded-full bg-brand-tiffany/30" />
          </div>
          <p className="font-serif text-2xl sm:text-3xl lg:text-4xl text-brand-mist leading-snug italic" data-testid="ripple-statement">
            Continuing the ripple of good energy and love,{" "}
            <span className="text-brand-gold not-italic">one person at a time.</span>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Session Teaser Section
const SessionTeaserSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="pt-24 bg-brand-dark relative overflow-hidden" ref={ref}>
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={fadeInUp}
        >
          <Link
            to="/the-session"
            className="group block relative overflow-hidden rounded-sm border border-brand-gold/30 hover:border-brand-gold/70 transition-all duration-500"
            data-testid="session-teaser-card"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-brand-gold/10 via-brand-dark to-brand-blue/20 group-hover:from-brand-gold/15 transition-all duration-700" />
            <div className="relative p-8 md:p-12 text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 border border-brand-gold/40 rounded-full mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-gold animate-pulse" />
                <span className="font-sans text-xs tracking-[0.15em] uppercase text-brand-gold">Private Coaching</span>
              </div>
              <h3 className="font-serif text-3xl md:text-4xl text-brand-mist mb-4" data-testid="session-teaser-heading">
                The <span className="italic text-brand-gold">Session</span>
              </h3>
              <p className="font-sans text-base text-brand-mist/70 max-w-xl mx-auto mb-8">
                Three hours. One breakthrough. Not motivation, training. For people who already
                won and can't feel the win.
              </p>
              <div className="inline-flex items-center gap-3 text-brand-gold group-hover:gap-5 transition-all duration-300">
                <span className="font-sans text-base tracking-wide">See how it works</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// AI Teaser Section
const AiTeaserSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="py-24 bg-brand-dark relative overflow-hidden" ref={ref}>
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={fadeInUp}
        >
          <Link
            to="/brandi-jo-ai"
            className="group block relative overflow-hidden rounded-sm border border-brand-tiffany/30 hover:border-brand-gold/60 transition-all duration-500"
            data-testid="ai-teaser-card"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-brand-tiffany/10 via-brand-dark to-brand-gold/5 group-hover:from-brand-gold/10 transition-all duration-700" />
            <div className="relative p-8 md:p-12 text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 border border-brand-tiffany/40 rounded-full mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-tiffany animate-pulse" />
                <span className="font-sans text-xs tracking-[0.15em] uppercase text-brand-tiffanydeep">Free</span>
              </div>
              <Sparkles className="w-10 h-10 text-brand-gold mx-auto mb-5" strokeWidth={1.5} />
              <h3 className="font-serif text-3xl md:text-4xl text-brand-mist mb-4" data-testid="ai-teaser-heading">
                Get to know <span className="italic text-brand-gold">Brandi Jo AI</span>
              </h3>
              <p className="font-sans text-base text-brand-mist/70 max-w-xl mx-auto mb-8">
                Learn everything Brandi Jo knows. Life, business, golf and everything
                in between. Free to chat, any hour of the day.
              </p>
              <div className="inline-flex items-center gap-3 text-brand-gold group-hover:gap-5 transition-all duration-300">
                <span className="font-sans text-base tracking-wide">Start chatting free</span>
                <ArrowRight className="w-5 h-5" />
              </div>
            </div>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

// Footer Section
const FooterSection = () => {
  return (
    <footer className="py-12 bg-brand-dark border-t border-brand-blue/30">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-center md:text-left">
            <p className="font-serif text-xl text-brand-mist mb-2">Brandi Jo Newman</p>
          </div>
          <div className="flex items-center gap-6">
            <a
              href="https://instagram.com/brandijonewman"
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
              data-testid="footer-instagram"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              href="https://linkedin.com/in/brandijonewman"
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
              data-testid="footer-linkedin"
            >
              <Linkedin className="w-5 h-5" />
            </a>
            <a
              href="https://youtube.com/@brandijonewman365"
              target="_blank"
              rel="noopener noreferrer"
              className="social-link"
              data-testid="footer-youtube"
            >
              <Youtube className="w-5 h-5" />
            </a>
            <a
              href="mailto:me@brandijo.com"
              className="social-link"
              data-testid="footer-email"
            >
              <Mail className="w-5 h-5" />
            </a>
          </div>
          <p className="font-sans text-xs text-brand-mist/30">
            © {new Date().getFullYear()} Brandi Jo Newman. All rights reserved. &nbsp;·&nbsp;{" "}
            <Link to="/brandi-jo-ai" className="hover:text-brand-gold transition-colors underline" data-testid="footer-ai-link">Brandi Jo AI</Link> &nbsp;·&nbsp;{" "}
            <Link to="/terms" className="hover:text-brand-gold transition-colors underline" data-testid="footer-terms-link">Terms</Link> &nbsp;·&nbsp;{" "}
            <Link to="/privacy" className="hover:text-brand-gold transition-colors underline" data-testid="footer-privacy-link">Privacy</Link>
          </p>
        </div>
      </div>
    </footer>
  );
};

// Home Page
function HomePage() {
  return (
    <div className="App">
      <div className="noise-overlay" />
      <Navigation />
      <HeroSection />
      <AboutSection />
      <JourneySection />
      <PhilosophySection />
      <TestimonialsSection />
      <RippleSection />
      <NowChapterSection />
      <AiTeaserSection />
      <NewsletterSection />
      <ContactSection />
      <FooterSection />
    </div>
  );
}

// Scroll to top on every route change
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: "instant", block: "start" });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

// Main App — Router
function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Toaster position="top-right" theme="dark" />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/live-a-great-story" element={<Navigate to="/" replace />} />
        <Route path="/about" element={<Navigate to="/" replace />} />
        <Route path="/coaching" element={<Navigate to="/" replace />} />
        <Route path="/yips" element={<Navigate to="/" replace />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/agents" element={<Navigate to="/" replace />} />
        <Route path="/brandi-jo-ai" element={<AiPage />} />
        <Route path="/the-session" element={<Navigate to="/" replace />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/payment/cancel" element={<PaymentCancelPage />} />
        <Route path="/not-an-asshole" element={<Navigate to="/" replace />} />
        <Route path="/self-devotion" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
